import { http, setBackOfficeApiBaseUrl } from '../../http';
import {
    RequestGetMyAccount,
    ResponseGetMyAccount,
    RequestGetUsers,
    ResponseGetUsers,
    RequestCreateUser,
    ResponseCreateUser,
    ResponseGetUser,
    RequestUpdateUser,
    UserAccessPermissions,
} from './types';
import { __ } from '@/js/stores/expressions';
import { useCustomConfig } from '@/js/composables/useCustomAxiosRequestConfig';
import { ResponseGetRoles } from '../role/types';

export const getMyAccount = () => {
    return http
        .get<RequestGetMyAccount, ResponseGetMyAccount>('users/my-account')
        .then((response) => {
            setBackOfficeApiBaseUrl(response.data.backOfficeApiBaseUrl);
            return response.data;
        })
        .catch((error) => {
            // Log the error for debugging purposes
            console.error('Error fetching my account:', error);

            // Rethrow the error so it can be handled by the caller
            throw error;
        });
};

export const getUsers = async (request: RequestGetUsers): Promise<ResponseGetUsers> => {
    return http
        .get<ResponseGetUsers>(
            '/user-management/users',
            useCustomConfig({
                params: {
                    page: request.page,
                    pageSize: request.pageSize,
                },
                errorMessage: {
                    400: __('error-load-users'),
                    500: __('error-load-users'),
                    502: __('error-load-users'),
                },
            })
        )
        .then((response) => response.data);
};

export const getUsersByFilter = async (filter: string): Promise<ResponseGetUsers> => {
    if (filter.length < 2) {
        return { users: [] } as ResponseGetUsers;
    }

    return http
        .get<ResponseGetUsers>('/users/lookup', {
            params: {
                query: filter,
                limit: 1000,
            },
        })
        .then((response) => response.data)
        .catch((error) => {
            if (error.response?.status === 404) {
                return { users: [] } as ResponseGetUsers;
            }
            throw error;
        });
};

export const createUser = async (request: RequestCreateUser): Promise<ResponseCreateUser> => {
    return http
        .post<ResponseCreateUser>(
            '/user-management/user',
            request,
            useCustomConfig({
                errorMessage: {
                    409: __('error-user-already-exists'),
                    400: __('error-create-user'),
                    500: __('error-create-user'),
                    502: __('error-create-user'),
                },
            })
        )
        .then((response) => response.data);
};

export const deleteUser = async (userId: string): Promise<void> => {
    return http
        .delete(
            '/user-management/user',
            useCustomConfig({
                params: {
                    userId,
                },
                errorMessage: {
                    500: __('error-delete-user'),
                    502: __('error-delete-user'),
                },
            })
        )
        .then(() => undefined);
};

// Backend returns bitwise values as strings in some payloads; coerce to number
// so that strict equality checks (e.g. change detection) compare like-for-like.
const coerceFlagRecord = (record: Record<string, unknown> | undefined): Record<string, number> =>
    Object.fromEntries(Object.entries(record ?? {}).map(([key, value]) => [key, Number(value)]));

const normalizeAccess = (data: ResponseGetUser): ResponseGetUser => {
    // Lightweight responses carry only access.hasOverride (no permission layers).
    if (!data.access?.permissions) return data;
    const { permissions } = data.access;
    const normalized: UserAccessPermissions = {
        role: coerceFlagRecord(permissions.role),
        user: coerceFlagRecord(permissions.user),
        effective: coerceFlagRecord(permissions.effective),
    };
    return { ...data, access: { ...data.access, permissions: normalized } };
};

export const getUser = async (
    userId: string,
    opts?: { detailed?: boolean }
): Promise<ResponseGetUser> => {
    return http
        .get<ResponseGetUser>(
            `/user-management/user/${userId}`,
            useCustomConfig({
                params: opts?.detailed ? { detailed: true } : undefined,
                errorMessage: {
                    404: __('error-user-not-found'),
                    500: __('error-load-user'),
                    502: __('error-load-user'),
                },
            })
        )
        .then((response) => normalizeAccess(response.data));
};

export const updateUser = async (request: RequestUpdateUser): Promise<void> => {
    const { userId, ...payload } = request;

    return http
        .put(
            `/user-management/user/${userId}`,
            payload,
            useCustomConfig({
                errorMessage: {
                    403: __('error-update-user'),
                    500: __('error-update-user'),
                    502: __('error-update-user'),
                },
            })
        )
        .then(() => undefined);
};

// Reverts the user to its tenant role, dropping all personal overrides.
// Idempotent on the backend (200 even when no overrides existed).
export const removeUserOverrides = async (userId: string): Promise<void> => {
    return http
        .delete(
            `/user-management/users/${userId}/overrides`,
            useCustomConfig({
                errorMessage: {
                    403: __('error-update-user'),
                    404: __('error-user-not-found'),
                    500: __('error-update-user'),
                    502: __('error-update-user'),
                },
            })
        )
        .then(() => undefined);
};

export const getRoles = async (): Promise<ResponseGetRoles> => {
    return http
        .get<ResponseGetRoles>(
            '/user-management/roles',
            useCustomConfig({
                errorMessage: {
                    500: __('error-load-roles'),
                    502: __('error-load-roles'),
                },
            })
        )
        .then((response) => response.data);
};
